import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { customersApi, companiesApi, fondationsApi } from '../../services/api';
import Table from '../../components/Table';
import { Search, Eye } from 'lucide-react';
import type { Customer } from '../../types';

const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [stateFilter, setStateFilter] = useState<string>('');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [fondationFilter, setFondationFilter] = useState<string>('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesApi.getAll();
      return response.data;
    },
  });

  const { data: fondationsData } = useQuery({
    queryKey: ['fondations'],
    queryFn: async () => {
      const response = await fondationsApi.getAll();
      return response.data;
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['customers', debouncedSearch, page, perPage, stateFilter, companyFilter, fondationFilter],
    queryFn: async () => {
      const response = await customersApi.getAll({
        search: debouncedSearch,
        page,
        state: stateFilter ? parseInt(stateFilter) : undefined,
        company: companyFilter ? parseInt(companyFilter) : undefined,
        fondation: fondationFilter || undefined,
        per_page: perPage,
      });
      return response.data;
    },
  });

  const columns = [
    {
      header: 'Nom complet',
      accessor: (row: Customer) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.full_name}</div>
          <div className="text-sm text-gray-500">{row.login}</div>
        </div>
      ),
    },
    {
      header: 'État',
      accessor: (row: Customer) => (
        <span className="text-sm text-gray-900">{row.state_name || `État ${row.state}`}</span>
      ),
    },
    {
      header: 'Société',
      accessor: (row: Customer) => (
        <span className="text-sm text-gray-900">{row.company_name || '-'}</span>
      ),
    },
    {
      header: 'Agent',
      accessor: (row: Customer) => (
        <span className="text-sm text-gray-900">{row.agent_name || '-'}</span>
      ),
    },
    {
      header: "Date d'inscription",
      accessor: (row: Customer) => (
        <span className="text-sm text-gray-900">
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString('fr-FR') : '-'}
        </span>
      ),
    },
    {
      header: 'Dernière activité',
      accessor: (row: Customer) => (
        <span className="text-sm text-gray-900">
          {row.last_activity_at ? new Date(row.last_activity_at).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }) : '-'}
        </span>
      ),
    },
    {
      header: 'Fondation',
      accessor: (row: Customer) => (
        <span className="text-sm text-gray-900">{row.fondation || '-'}</span>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: Customer) => (
        <button
          onClick={() => navigate(`/customers/${row.uuid}`)}
          className="text-primary-600 hover:text-primary-900 transition"
        >
          <Eye className="h-5 w-5" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
        <p className="text-gray-600 mt-1">Gestion des clients LPP</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, AVS..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>

          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Tous les états</option>
            <option value="1">Contrôle administratif</option>
            <option value="2">Soumission institutions</option>
            <option value="3">Attente réponses</option>
            <option value="4">Dossier clôturé</option>
          </select>

          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Toutes les sociétés</option>
            {companiesData?.data?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>

          <select
            value={fondationFilter}
            onChange={(e) => setFondationFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">Toutes les fondations</option>
            {fondationsData?.data?.map((fondation) => (
              <option key={fondation.id} value={fondation.name}>
                {fondation.name}
              </option>
            ))}
          </select>

          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(parseInt(e.target.value));
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="10">10 par page</option>
            <option value="15">15 par page</option>
            <option value="25">25 par page</option>
            <option value="50">50 par page</option>
            <option value="100">100 par page</option>
          </select>
        </div>
      </div>

      <Table
        data={data?.data || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="Aucun client trouvé"
        pagination={
          data?.meta
            ? {
                currentPage: data.meta.current_page,
                totalPages: data.meta.last_page,
                total: data.meta.total,
                from: data.meta.from,
                to: data.meta.to,
                onPageChange: setPage,
              }
            : undefined
        }
      />
    </div>
  );
};

export default CustomerList;
